import _typeof from "@babel/runtime/helpers/typeof";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _get from "@babel/runtime/helpers/get";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
import React from 'react';
import { getTableContainerWidth } from '@atlaskit/editor-common/node-width';
import ReactNodeView from '@atlaskit/editor-common/react-node-view';
import { WithPluginState } from '@atlaskit/editor-common/with-plugin-state';
import { DOMSerializer } from '@atlaskit/editor-prosemirror/model';
import { akEditorTableNumberColumnWidth } from '@atlaskit/editor-shared-styles';
import { TableMap } from '@atlaskit/editor-tables/table-map';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { pluginConfig as getPluginConfig } from '../create-plugin-config';
import { pluginKey as tableDragAndDropPluginKey } from '../pm-plugins/drag-and-drop';
import { getPluginState } from '../pm-plugins/plugin-factory';
import { pluginKey } from '../pm-plugins/plugin-key';
import { pluginKey as tableResizingPluginKey } from '../pm-plugins/table-resizing';
import { generateColgroup } from '../pm-plugins/table-resizing/utils';
import { pluginKey as tableWidthPluginKey } from '../pm-plugins/table-width';
import { isTableNested } from '../utils';
import TableComponent from './TableComponent';
import { TableComponentWithSharedState } from './TableComponentWithSharedState';
var tableAttributes = function tableAttributes(node) {
  return {
    'data-number-column': node.attrs.isNumberColumnEnabled,
    'data-layout': node.attrs.layout,
    'data-autosize': node.attrs.__autoSize,
    'data-table-local-id': node.attrs.localId || '',
    'data-table-width': node.attrs.width
  };
};
var getInlineWidth = function getInlineWidth(node, options, state, pos) {
  // provide a width for tables when custom table width is supported
  // this is to ensure 'responsive' tables (colgroup widths are undefined) become fixed to
  // support screen size adjustments
  var shouldHaveInlineWidth = (options === null || options === void 0 ? void 0 : options.isTableResizingEnabled) && !isTableNested(state, pos);
  var widthValue = getTableContainerWidth(node);
  if (node.attrs.isNumberColumnEnabled) {
    widthValue -= akEditorTableNumberColumnWidth;
  }
  return shouldHaveInlineWidth ? widthValue : undefined;
};
var handleInlineTableWidth = function handleInlineTableWidth(table, width) {
  if (!table || !width) {
    return;
  }
  table.style.setProperty('width', "".concat(width, "px"));
};
var toDOM = function toDOM(node, props) {
  var colgroup = '';
  if (props.allowColumnResizing) {
    colgroup = ['colgroup', {}].concat(_toConsumableArray(generateColgroup(node)));
  }
  return ['table', tableAttributes(node), colgroup, ['tbody', 0]];
};
var TableView = /*#__PURE__*/function (_ReactNodeView) {
  _inherits(TableView, _ReactNodeView);
  var _super = _createSuper(TableView);
  function TableView(props) {
    var _this;
    _classCallCheck(this, TableView);
    _this = _super.call(this, props.node, props.view, props.getPos, props.portalProviderAPI, props.eventDispatcher, props, undefined, undefined, undefined, props.hasIntlContext);
    _defineProperty(_assertThisInitialized(_this), "getNode", function () {
      return _this.node;
    });
    _defineProperty(_assertThisInitialized(_this), "hasHoveredRows", false);
    _this.getPos = props.getPos;
    _this.eventDispatcher = props.eventDispatcher;
    _this.options = props.options;
    return _this;
  }
  _createClass(TableView, [{
    key: "getContentDOM",
    value: function getContentDOM() {
      var rendered = DOMSerializer.renderSpec(document, toDOM(this.node, this.reactComponentProps));
      if (rendered.dom) {
        var _this$options;
        this.table = rendered.dom;
        // Preserve Table Width cannot have inline width set on the table
        if (!((_this$options = this.options) !== null && _this$options !== void 0 && _this$options.isTableScalingEnabled)) {
          var tableInlineWidth = getInlineWidth(this.node, this.reactComponentProps.options, this.reactComponentProps.view.state, this.reactComponentProps.getPos());
          if (tableInlineWidth) {
            handleInlineTableWidth(this.table, tableInlineWidth);
          }
        }
      }
      return rendered;
    }
  }, {
    key: "setDomAttrs",
    value: function setDomAttrs(node) {
      var _this2 = this,
        _tableWidthPluginKey$;
      if (!this.table) {
        return;
      }
      var attrs = tableAttributes(node);
      Object.keys(attrs).forEach(function (attr) {
        _this2.table.setAttribute(attr, attrs[attr]);
      });

      // handle inline style when table been resized
      var tableInlineWidth = getInlineWidth(node, this.reactComponentProps.options, this.view.state, this.getPos());
      var isTableResizing = (_tableWidthPluginKey$ = tableWidthPluginKey.getState(this.view.state)) === null || _tableWidthPluginKey$ === void 0 ? void 0 : _tableWidthPluginKey$.resizing;
      if (!isTableResizing && tableInlineWidth) {
        handleInlineTableWidth(this.table, tableInlineWidth);
      }
    }
  }, {
    key: "render",
    value: function render(props, forwardRef) {
      var _this3 = this;
      if (getBooleanFF('platform.editor.table.use-shared-state-hook')) {
        return /*#__PURE__*/React.createElement(TableComponentWithSharedState, {
          forwardRef: forwardRef,
          getNode: this.getNode,
          view: props.view,
          options: props.options,
          eventDispatcher: props.eventDispatcher,
          api: props.pluginInjectionApi,
          allowColumnResizing: props.allowColumnResizing,
          allowControls: props.allowControls,
          getPos: props.getPos,
          getEditorFeatureFlags: props.getEditorFeatureFlags,
          dispatchAnalyticsEvent: props.dispatchAnalyticsEvent
        });
      }

      // TODO: ED-15663
      // Please, do not copy or use this kind of code below
      // @ts-ignore
      var fakePluginKey = {
        key: 'widthPlugin$',
        getState: function getState(state) {
          return state['widthPlugin$'];
        }
      };

      // TODO: ED-15663
      // Please, do not copy or use this kind of code below
      // @ts-ignore
      var fakeMediaPluginKey = {
        key: 'mediaPlugin$',
        getState: function getState(state) {
          return state['mediaPlugin$'];
        }
      };
      return /*#__PURE__*/React.createElement(WithPluginState, {
        plugins: {
          pluginState: pluginKey,
          tableResizingPluginState: tableResizingPluginKey,
          tableWidthPluginState: tableWidthPluginKey,
          widthPlugin: fakePluginKey,
          mediaState: fakeMediaPluginKey,
          tableDragAndDropState: tableDragAndDropPluginKey
        },
        editorView: props.view,
        render: function render(pluginStates) {
          var tableResizingPluginState = pluginStates.tableResizingPluginState,
            tableWidthPluginState = pluginStates.tableWidthPluginState,
            pluginState = pluginStates.pluginState,
            mediaState = pluginStates.mediaState;
          var containerWidth = props.getEditorContainerWidth();
          var isTableResizing = tableWidthPluginState === null || tableWidthPluginState === void 0 ? void 0 : tableWidthPluginState.resizing;
          var isResizing = Boolean((tableResizingPluginState === null || tableResizingPluginState === void 0 ? void 0 : tableResizingPluginState.dragging) || isTableResizing);

          /**
           *  ED-19810
           *  There is a getPos issue coming from this code. We need to apply this workaround for now and apply a patch
           *  directly to confluence since this bug is now in production.
           */
          var tablePos;
          try {
            tablePos = props.getPos ? props.getPos() : undefined;
          } catch (e) {
            tablePos = undefined;
          }
          var tableActive = tablePos === pluginState.tablePos && !isTableResizing;
          return /*#__PURE__*/React.createElement(TableComponent, {
            view: props.view,
            allowColumnResizing: props.allowColumnResizing,
            eventDispatcher: props.eventDispatcher,
            getPos: props.getPos,
            isMediaFullscreen: mediaState === null || mediaState === void 0 ? void 0 : mediaState.isFullscreen,
            options: props.options,
            allowControls: pluginState.pluginConfig.allowControls,
            isHeaderRowEnabled: pluginState.isHeaderRowEnabled,
            isHeaderColumnEnabled: pluginState.isHeaderColumnEnabled,
            isDragAndDropEnabled: pluginState.isDragAndDropEnabled,
            isTableScalingEnabled: pluginState.isTableScalingEnabled,
            tableActive: tableActive,
            ordering: pluginState.ordering,
            isResizing: isResizing,
            getNode: _this3.getNode,
            containerWidth: containerWidth,
            contentDOM: forwardRef,
            getEditorFeatureFlags: props.getEditorFeatureFlags,
            dispatchAnalyticsEvent: props.dispatchAnalyticsEvent,
            pluginInjectionApi: props.pluginInjectionApi
          });
        }
      });
    }
  }, {
    key: "viewShouldUpdate",
    value: function viewShouldUpdate(nextNode) {
      var _getPluginState = getPluginState(this.view.state),
        hoveredRows = _getPluginState.hoveredRows;
      var hoveredRowsChanged = !!(hoveredRows !== null && hoveredRows !== void 0 && hoveredRows.length) !== this.hasHoveredRows;
      if (nextNode.attrs.isNumberColumnEnabled && hoveredRowsChanged) {
        this.hasHoveredRows = !!(hoveredRows !== null && hoveredRows !== void 0 && hoveredRows.length);
        return true;
      }
      var node = this.getNode();
      if (_typeof(node.attrs) !== _typeof(nextNode.attrs)) {
        return true;
      }
      var attrKeys = Object.keys(node.attrs);
      var nextAttrKeys = Object.keys(nextNode.attrs);
      if (attrKeys.length !== nextAttrKeys.length) {
        return true;
      }
      var tableMap = TableMap.get(node);
      var nextTableMap = TableMap.get(nextNode);
      if (tableMap.width !== nextTableMap.width) {
        return true;
      }
      return attrKeys.some(function (key) {
        return node.attrs[key] !== nextNode.attrs[key];
      });
    }
  }, {
    key: "ignoreMutation",
    value: function ignoreMutation(mutation) {
      var type = mutation.type,
        _mutation$target = mutation.target,
        nodeName = _mutation$target.nodeName,
        firstChild = _mutation$target.firstChild;
      if (type === 'selection' && (nodeName === null || nodeName === void 0 ? void 0 : nodeName.toUpperCase()) === 'DIV' && (firstChild === null || firstChild === void 0 ? void 0 : firstChild.nodeName.toUpperCase()) === 'TABLE') {
        return false;
      }

      // ED-16668
      // Do not remove this fixes an issue with windows firefox that relates to
      // the addition of the shadow sentinels
      if (type === 'selection' && (nodeName === null || nodeName === void 0 ? void 0 : nodeName.toUpperCase()) === 'TABLE' && ((firstChild === null || firstChild === void 0 ? void 0 : firstChild.nodeName.toUpperCase()) === 'COLGROUP' || (firstChild === null || firstChild === void 0 ? void 0 : firstChild.nodeName.toUpperCase()) === 'SPAN')) {
        return false;
      }
      return true;
    }
  }, {
    key: "destroy",
    value: function destroy() {
      var _this$eventDispatcher;
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      (_this$eventDispatcher = this.eventDispatcher) === null || _this$eventDispatcher === void 0 || _this$eventDispatcher.emit('TABLE_DELETED', this.node);
      _get(_getPrototypeOf(TableView.prototype), "destroy", this).call(this);
    }
  }]);
  return TableView;
}(ReactNodeView);
export { TableView as default };
export var createTableView = function createTableView(node, view, getPos, portalProviderAPI, eventDispatcher, getEditorContainerWidth, getEditorFeatureFlags, dispatchAnalyticsEvent, pluginInjectionApi) {
  var _getPluginState2 = getPluginState(view.state),
    pluginConfig = _getPluginState2.pluginConfig,
    isBreakoutEnabled = _getPluginState2.isBreakoutEnabled,
    isFullWidthModeEnabled = _getPluginState2.isFullWidthModeEnabled,
    wasFullWidthModeEnabled = _getPluginState2.wasFullWidthModeEnabled,
    isTableResizingEnabled = _getPluginState2.isTableResizingEnabled,
    isDragAndDropEnabled = _getPluginState2.isDragAndDropEnabled,
    isTableScalingEnabled = _getPluginState2.isTableScalingEnabled;
  var _getPluginConfig = getPluginConfig(pluginConfig),
    allowColumnResizing = _getPluginConfig.allowColumnResizing,
    allowControls = _getPluginConfig.allowControls;
  var hasIntlContext = true;
  return new TableView({
    node: node,
    view: view,
    allowColumnResizing: allowColumnResizing,
    allowControls: allowControls,
    portalProviderAPI: portalProviderAPI,
    eventDispatcher: eventDispatcher,
    getPos: getPos,
    options: {
      isBreakoutEnabled: isBreakoutEnabled,
      isFullWidthModeEnabled: isFullWidthModeEnabled,
      wasFullWidthModeEnabled: wasFullWidthModeEnabled,
      isTableResizingEnabled: isTableResizingEnabled,
      isDragAndDropEnabled: isDragAndDropEnabled,
      isTableScalingEnabled: isTableScalingEnabled
    },
    getEditorContainerWidth: getEditorContainerWidth,
    getEditorFeatureFlags: getEditorFeatureFlags,
    dispatchAnalyticsEvent: dispatchAnalyticsEvent,
    hasIntlContext: hasIntlContext,
    pluginInjectionApi: pluginInjectionApi
  }).init();
};